import React, { useRef, useState } from "react";
import { ContextMenu } from "primereact/contextmenu";
import { Dialog } from "primereact/dialog";
import useTabId from "hooks/dashboard/useTabId";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import useDashboardStore from "store/dashboard-store";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import DashboardComponentCard from "./GroupComponentCard";
import DashboardComponentConfiguratorNew, {
  ConfiguratorModes,
} from "./GroupComponentConfiguratorNew";

interface DashboardComponentProps {
  groupId: string;
  componentId: string;
  component: IDashboardComponent;
  layoutType: DashboardLayoutType;
}

const DashboardComponent = ({
  groupId,
  componentId,
  component,
  layoutType,
}: DashboardComponentProps) => {
  const cm = useRef<ContextMenu>(null);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const updateComponent = useDashboardStore(state => state.updateComponent);
  const deleteComponent = useDashboardStore(state => state.removeComponent);
  const moveComponent = useDashboardStore(state => state.moveComponent);
  const getTab = useDashboardStore(state => state.getTab);
  const tabId = useTabId()
  const [isConfiguratorVisible, setIsConfiguratorVisible] = useState<boolean>(false);
  const [isMoveToOtherGroupVisible, setIsMoveToOtherGroupVisible] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<number>(0);
  const currentTab = getTab(tabId);
  const currentGroups = currentTab?.componentGroups ?? [];

  const handleDeleteComponent = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: "Confirm Action",
      message: `Are you sure you want to delete this component?`,
      confirmedCallback: () => {
        deleteComponent(groupId, componentId, tabId);
      },
    });
  };

  const items = [
    {
      label: "Edit Config",
      icon: "pi pi-pencil",
      command: () => {
        setIsConfiguratorVisible(true);
      },
    },
    {
      label: "Delete",
      icon: "pi pi-times",
      command: () => {
        handleDeleteComponent();
      },
    },
    {
      label: "Move to other group",
      icon: "pi pi-arrow-right",
      command: () => {
        setIsMoveToOtherGroupVisible(true);
      },
    },
  ];

  const handleMoveToGroup = () => {
    if (selectedGroup) {
      moveComponent(groupId, componentId, selectedGroup, tabId);
      setIsMoveToOtherGroupVisible(false);
    }
  };

  const handleComponentChange = (newConfig: IDashboardComponent) => {
    updateComponent(groupId, componentId, newConfig, tabId);
    setIsConfiguratorVisible(false);
  };

  return (
    <div className="h-full">
      {isEditMode && <ContextMenu model={items} ref={cm} />}
      <div onContextMenu={e => cm.current?.show(e)} className="h-full">
        <DashboardComponentCard component={component} />
      </div>
      {isConfiguratorVisible && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={isConfiguratorVisible}
          mode={ConfiguratorModes.EDIT}
          setIsDialogVisible={setIsConfiguratorVisible}
          component={component}
          onComponentChange={handleComponentChange}
          layoutType={layoutType}
        />
      )}
      {isMoveToOtherGroupVisible && (
        <Dialog
          header="Move to other group"
          visible={isMoveToOtherGroupVisible}
          onHide={() => setIsMoveToOtherGroupVisible(false)}
          draggable={false}
          footer={
            <div>
              <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={() => setIsMoveToOtherGroupVisible(false)}
              />
              <Button
                label="Move"
                icon="pi pi-check"
                onClick={handleMoveToGroup}
                disabled={!selectedGroup}
              />
            </div>
          }
        >
          <div>
            <label htmlFor="currentGroups">Select group</label>
            <Dropdown
              id="currentGroups"
              value={selectedGroup}
              options={currentGroups.map((group, index) => ({
                label: group.title || `Group ${index + 1}`,
                value: group.id,
              }))}
              onChange={e => setSelectedGroup(e.value)}
              placeholder="Select a group"
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default DashboardComponent;
